import { useState, useEffect, useMemo, useRef } from "react";
import { Link as LinkIcon, ImageOff, Plus, Trash2, X } from "lucide-react";
import { api } from "../api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useConfirm } from "../components/ConfirmDialog.jsx";
import { useToast } from "../components/Toast.jsx";
import { useAuth } from "../contexts/AuthContext.jsx";

const cardShadow = { boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)" };

function SectionLabel({ children }) {
  return <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{children}</p>;
}

function ReadOnlyNote() {
  return <p className="text-xs text-slate-400 mb-3">Справочник редактируется только на центральном сервере.</p>;
}

// Общий каталог наследуется всеми точками; этот выбор позволяет вместо этого завести запись
// только для одной конкретной точки — не убирает общие записи оттуда, только добавляет свою.
function LocationPicker({ value, onChange, className }) {
  const [options, setOptions] = useState([]);
  useEffect(() => { api.listLocations().then(setOptions).catch(() => {}); }, []);
  return (
    <select
      className={className || "h-8 text-xs rounded-md border border-slate-200 bg-white px-2"}
      value={value}
      onChange={e => onChange(e.target.value)}
      title="Общий (везде) или только для одной точки"
    >
      <option value="">Общий (везде)</option>
      {options.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
    </select>
  );
}

function LocationBadge({ location }) {
  if (!location) return null;
  return <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 rounded-full px-1.5 py-0.5">{location}</span>;
}

// Версия Electron + когда точка последний раз выходила на связь (см. sync.js runSync()
// heartbeat) — красным, если давно не было (>36ч, дольше обычного цикла авто-обновления раз в
// 4ч + периода до перезапуска точки) или версия вообще ни разу не приходила (старый клиент без
// этого поля). Не блокирует ничего, просто видимость вместо тишины.
function LocationHeartbeatBadge({ item }) {
  if (!item.last_seen_version && !item.last_seen_at) return null;
  const staleMs = item.last_seen_at ? Date.now() - new Date(item.last_seen_at).getTime() : Infinity;
  const stale = staleMs > 36 * 60 * 60 * 1000;
  const lastSeenLabel = item.last_seen_at ? new Date(item.last_seen_at).toLocaleString("ru-RU") : "никогда";
  return (
    <span
      className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${stale ? "text-red-600 bg-red-50" : "text-emerald-600 bg-emerald-50"}`}
      title={`Последний раз на связи: ${lastSeenLabel}`}
    >
      v{item.last_seen_version || "?"}
    </span>
  );
}

// "__all__" — без фильтра (по умолчанию), "__common__" — только общий каталог (location=null),
// иначе — имя конкретной точки. Используется вкладками ниже, чтобы можно было визуально
// проверить, что именно видно на каждой точке после синхронизации.
function matchesLocationFilter(item, filter) {
  if (!filter || filter === "__all__") return true;
  if (filter === "__common__") return !item.location;
  return item.location === filter;
}

function LocationFilterSelect({ value, onChange }) {
  const [options, setOptions] = useState([]);
  useEffect(() => { api.listLocations().then(setOptions).catch(() => {}); }, []);
  return (
    <select
      className="h-8 text-xs rounded-full border border-slate-200 bg-white px-3 ml-auto"
      value={value}
      onChange={e => onChange(e.target.value)}
      title="Показать справочник как видно на конкретной точке"
    >
      <option value="__all__">Все точки</option>
      <option value="__common__">Общий</option>
      {options.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
    </select>
  );
}

// ── Product group tree helpers (shared by insoles/accessories) ───────────────
function buildGroupTree(flat) {
  const byParent = {};
  flat.forEach(g => {
    const key = g.parentId || "root";
    (byParent[key] ||= []).push(g);
  });
  const attach = (parentKey) => (byParent[parentKey] || []).map(g => ({ ...g, children: attach(g.id) }));
  return attach("root");
}

function flattenGroupOptions(tree, depth = 0) {
  return tree.flatMap(g => [{ id: g.id, name: g.name, depth }, ...flattenGroupOptions(g.children, depth + 1)]);
}

function groupPath(groups, groupId) {
  const byId = new Map(groups.map(g => [g.id, g]));
  const parts = [];
  let cur = groupId;
  while (cur) {
    const g = byId.get(cur);
    if (!g) break;
    parts.unshift(g.name);
    cur = g.parentId;
  }
  return parts.join(" / ");
}

// ── Group management tree — create/delete groups & subgroups, up to 3 levels ─
function GroupManager({ category, groups, onGroupsChange }) {
  const { user } = useAuth();
  const isCentral = !!user?.isCentral;
  const [addingUnder, setAddingUnder] = useState(null); // group id, "root", or null
  const [newName, setNewName] = useState("");
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { showToast, ToastElement } = useToast();

  const tree = useMemo(() => buildGroupTree(groups), [groups]);

  const startAdd = (parentId) => { setAddingUnder(parentId); setNewName(""); };

  const handleAdd = async (parentId) => {
    if (!newName.trim()) return;
    try {
      const created = await api.createProductGroup({ name: newName.trim(), category, parentId });
      onGroupsChange([...groups, created]);
      setAddingUnder(null);
      setNewName("");
    } catch (err) {
      showToast(err.response?.data?.error || "Ошибка");
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm("Удалить группу вместе с подгруппами? Товары внутри останутся, но окажутся без группы.", { danger: true }))) return;
    try {
      await api.deleteProductGroup(id);
      const toRemove = new Set([id]);
      let grew = true;
      while (grew) {
        grew = false;
        groups.forEach(g => {
          if (g.parentId && toRemove.has(g.parentId) && !toRemove.has(g.id)) { toRemove.add(g.id); grew = true; }
        });
      }
      onGroupsChange(groups.filter(g => !toRemove.has(g.id)));
    } catch {
      showToast("Ошибка удаления");
    }
  };

  const renderNode = (node, depth) => (
    <div key={node.id} style={{ marginLeft: depth * 18 }}>
      <div className="flex items-center gap-1.5 py-1 group/node">
        <span className="text-xs font-medium text-slate-700">{node.name}</span>
        {isCentral && (
          <div className="flex items-center gap-1 opacity-0 group-hover/node:opacity-100 transition-opacity">
            {depth < 2 && (
              <button onClick={() => startAdd(node.id)} className="text-slate-400 hover:text-blue-600" title="Добавить подгруппу">
                <Plus className="h-3 w-3" />
              </button>
            )}
            <button onClick={() => handleDelete(node.id)} className="text-slate-400 hover:text-red-600" title="Удалить группу">
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
      {addingUnder === node.id && (
        <div style={{ marginLeft: 18 }}>
          <GroupAddForm parentId={node.id} value={newName} onChange={setNewName} onSubmit={handleAdd} onCancel={() => setAddingUnder(null)} />
        </div>
      )}
      {node.children.map(c => renderNode(c, depth + 1))}
    </div>
  );

  return (
    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-3 mb-3">
      <div className="flex items-center justify-between mb-1">
        <SectionLabel>Группы</SectionLabel>
        {isCentral && (
          <button onClick={() => startAdd("root")} className="flex items-center gap-0.5 text-[11px] text-blue-500 hover:text-blue-700 font-semibold transition-colors">
            <Plus className="h-3 w-3" /> Группа
          </button>
        )}
      </div>
      {tree.length === 0 && addingUnder !== "root" && (
        <p className="text-[11px] text-slate-400">Групп нет — все товары в общем списке.</p>
      )}
      {tree.map(n => renderNode(n, 0))}
      {addingUnder === "root" && (
        <GroupAddForm parentId={null} value={newName} onChange={setNewName} onSubmit={handleAdd} onCancel={() => setAddingUnder(null)} />
      )}
      <ConfirmDialogElement />
      <ToastElement />
    </div>
  );
}

function GroupAddForm({ parentId, value, onChange, onSubmit, onCancel }) {
  return (
    <div className="flex items-center gap-1.5 mt-1 mb-1.5">
      <Input
        className="h-7 text-xs w-40"
        autoFocus
        placeholder="Название группы..."
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => e.key === "Enter" && onSubmit(parentId)}
      />
      <button onClick={() => onSubmit(parentId)} className="text-xs font-semibold text-blue-600 hover:text-blue-800">✓</button>
      <button onClick={onCancel} className="text-slate-400 hover:text-slate-600">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ── Single-select group picker for the "add product" forms ───────────────────
function GroupSelect({ groups, value, onChange }) {
  const options = useMemo(() => flattenGroupOptions(buildGroupTree(groups)), [groups]);
  if (options.length === 0) return null;
  return (
    <select
      className="h-8 px-2.5 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-700"
      value={value || ""}
      onChange={e => onChange(e.target.value || null)}
    >
      <option value="">Без группы</option>
      {options.map(o => (
        <option key={o.id} value={o.id}>{"— ".repeat(o.depth)}{o.name}</option>
      ))}
    </select>
  );
}

// ── Simple name-only catalogs (Точки продаж, Заболевания, Жалобы, Обувь, Нагрузка) ─
function CatalogTab({ title, load, create, remove, scopeByLocation = true, locationFilter, renderExtra }) {
  const { user } = useAuth();
  const isCentral = !!user?.isCentral;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { showToast, ToastElement } = useToast();

  useEffect(() => {
    load().then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    setError("");
    try {
      const item = await create(newName.trim(), newLocation || null);
      setItems(prev => [...prev, item]);
      setNewName("");
      setNewLocation("");
    } catch (err) {
      setError(err.response?.data?.error || "Ошибка");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm("Удалить?", { danger: true }))) return;
    try {
      await remove(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch {
      showToast("Ошибка удаления");
    }
  };

  return (
    <div className="rounded-2xl bg-white overflow-hidden" style={cardShadow}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{title}</span>
      </div>
      <div className="p-4 flex flex-col gap-3">
        {isCentral ? (
          <div className="flex items-center gap-2">
            <Input
              className="h-8 text-xs flex-1"
              placeholder="Название..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAdd()}
            />
            {scopeByLocation && (
              <LocationPicker value={newLocation} onChange={setNewLocation} className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 max-w-[160px]" />
            )}
            <Button size="sm" disabled={adding || !newName.trim()} onClick={handleAdd}>
              {adding ? "..." : "+ Добавить"}
            </Button>
          </div>
        ) : <ReadOnlyNote />}
        {error && <p className="text-xs text-red-600">{error}</p>}

        {(() => {
          const visible = scopeByLocation ? items.filter(i => matchesLocationFilter(i, locationFilter)) : items;
          if (loading) return <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />;
          if (visible.length === 0) return <p className="text-xs text-slate-400 text-center py-4">Список пуст</p>;
          return (
            <div className="flex flex-col">
              {visible.map(item => (
                <div key={item.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 group">
                  <span className="text-xs text-slate-700 flex items-center gap-1.5">
                    {item.name}
                    <LocationBadge location={item.location} />
                    {renderExtra && renderExtra(item)}
                  </span>
                  {isCentral && (
                    <button onClick={() => handleDelete(item.id)} className="text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
      </div>
      <ConfirmDialogElement />
      <ToastElement />
    </div>
  );
}

function ProductThumb({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="flex items-center justify-center bg-slate-50 text-slate-300 rounded-lg" style={{ width: 40, height: 40 }}>
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }
  return <img src={src} alt={alt} onError={() => setFailed(true)} className="rounded-lg object-cover" style={{ width: 40, height: 40 }} />;
}
// kept as the same component the old code called ExerciseThumb — see ExercisesTab below
const ExerciseThumb = ProductThumb;

// ── Стельки: фото, название, цена + группы ────────────────────────────────────
function InsolesTab({ locationFilter }) {
  const { user } = useAuth();
  const isCentral = !!user?.isCentral;
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", price: "", groupId: null, location: "" });
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { showToast, ToastElement } = useToast();

  useEffect(() => {
    Promise.all([
      api.listProducts().then(list => list.filter(p => p.category === "insole")),
      api.listProductGroups("insole"),
    ]).then(([products, groupList]) => { setItems(products); setGroups(groupList); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setAdding(true);
    setError("");
    try {
      const item = await api.createProduct({ name: form.name.trim(), price: form.price ? Number(form.price) : null, category: "insole", groupId: form.groupId, location: form.location || null });
      setItems(prev => [...prev, item]);
      setForm({ name: "", price: "", groupId: null, location: "" });
    } catch (err) {
      setError(err.response?.data?.error || "Ошибка");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm("Удалить стельку?", { danger: true }))) return;
    try {
      await api.deleteProduct(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch {
      showToast("Ошибка удаления");
    }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    items.filter(it => matchesLocationFilter(it, locationFilter)).forEach(it => {
      const key = it.groupId ? groupPath(groups, it.groupId) : "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    });
    return [...map.keys()]
      .sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)))
      .map(k => ({ label: k || "Без группы", items: map.get(k) }));
  }, [items, groups, locationFilter]);

  return (
    <div className="rounded-2xl bg-white overflow-hidden" style={cardShadow}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Стельки</span>
      </div>
      <div className="p-4 flex flex-col gap-3">
        {!loading && <GroupManager category="insole" groups={groups} onGroupsChange={setGroups} />}

        {isCentral ? (
          <div className="flex flex-wrap items-center gap-2">
            <Input className="h-8 text-xs flex-1" style={{ minWidth: 140 }} placeholder="Название..." value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <Input className="h-8 text-xs" style={{ width: 100 }} type="number" placeholder="Цена (₽)" value={form.price}
              onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
            <GroupSelect groups={groups} value={form.groupId} onChange={groupId => setForm(f => ({ ...f, groupId }))} />
            <LocationPicker value={form.location} onChange={location => setForm(f => ({ ...f, location }))} className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 max-w-[140px]" />
            <Button size="sm" disabled={adding || !form.name.trim()} onClick={handleAdd}>
              {adding ? "..." : "+ Добавить"}
            </Button>
          </div>
        ) : <ReadOnlyNote />}
        {error && <p className="text-xs text-red-600">{error}</p>}

        {loading ? (
          <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />
        ) : grouped.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">Список пуст</p>
        ) : (
          <div className="flex flex-col gap-3">
            {grouped.map(g => (
              <div key={g.label}>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{g.label}</p>
                <div className="flex flex-col">
                  {g.items.map(item => (
                    <div key={item.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 group">
                      <span className="text-xs text-slate-700 flex-1 flex items-center gap-1.5">{item.name}<LocationBadge location={item.location} /></span>
                      <span className="text-xs font-semibold text-emerald-600">
                        {item.price ? `${item.price.toLocaleString("ru-RU")} ₽` : "—"}
                      </span>
                      {isCentral && (
                        <button onClick={() => handleDelete(item.id)} className="text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ConfirmDialogElement />
      <ToastElement />
    </div>
  );
}

// ── Сопутствующие: название, артикул, цена + группы ───────────────────────────
function AccessoriesTab({ locationFilter }) {
  const { user } = useAuth();
  const isCentral = !!user?.isCentral;
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", article: "", price: "", groupId: null, location: "" });
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { showToast, ToastElement } = useToast();

  useEffect(() => {
    Promise.all([
      api.listProducts().then(list => list.filter(p => p.category === "accessory")),
      api.listProductGroups("accessory"),
    ]).then(([products, groupList]) => { setItems(products); setGroups(groupList); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setAdding(true);
    setError("");
    try {
      const item = await api.createProduct({ name: form.name.trim(), price: form.price ? Number(form.price) : null, category: "accessory", article: form.article, groupId: form.groupId, location: form.location || null });
      setItems(prev => [...prev, item]);
      setForm({ name: "", article: "", price: "", groupId: null, location: "" });
    } catch (err) {
      setError(err.response?.data?.error || "Ошибка");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm("Удалить товар?", { danger: true }))) return;
    try {
      await api.deleteProduct(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch {
      showToast("Ошибка удаления");
    }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    items.filter(it => matchesLocationFilter(it, locationFilter)).forEach(it => {
      const key = it.groupId ? groupPath(groups, it.groupId) : "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    });
    return [...map.keys()]
      .sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)))
      .map(k => ({ label: k || "Без группы", items: map.get(k) }));
  }, [items, groups, locationFilter]);

  return (
    <div className="rounded-2xl bg-white overflow-hidden" style={cardShadow}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Сопутствующие товары</span>
      </div>
      <div className="p-4 flex flex-col gap-3">
        {!loading && <GroupManager category="accessory" groups={groups} onGroupsChange={setGroups} />}

        {isCentral ? (
          <div className="flex flex-wrap items-center gap-2">
            <Input className="h-8 text-xs flex-1" style={{ minWidth: 140 }} placeholder="Название..." value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <Input className="h-8 text-xs" style={{ width: 100 }} placeholder="Артикул..." value={form.article}
              onChange={e => setForm(f => ({ ...f, article: e.target.value }))} />
            <Input className="h-8 text-xs" style={{ width: 90 }} type="number" placeholder="Цена (₽)" value={form.price}
              onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
            <GroupSelect groups={groups} value={form.groupId} onChange={groupId => setForm(f => ({ ...f, groupId }))} />
            <LocationPicker value={form.location} onChange={location => setForm(f => ({ ...f, location }))} className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 max-w-[140px]" />
            <Button size="sm" disabled={adding || !form.name.trim()} onClick={handleAdd}>
              {adding ? "..." : "+ Добавить"}
            </Button>
          </div>
        ) : <ReadOnlyNote />}
        {error && <p className="text-xs text-red-600">{error}</p>}

        {loading ? (
          <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />
        ) : grouped.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">Список пуст</p>
        ) : (
          <div className="flex flex-col gap-3">
            {grouped.map(g => (
              <div key={g.label}>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{g.label}</p>
                <div className="flex flex-col">
                  {g.items.map(item => (
                    <div key={item.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 group">
                      <span className="text-xs text-slate-700 flex-1 flex items-center gap-1.5">{item.name}<LocationBadge location={item.location} /></span>
                      <span className="text-[11px] text-slate-400">{item.article || "—"}</span>
                      <span className="text-xs font-semibold text-emerald-600">
                        {item.price ? `${item.price.toLocaleString("ru-RU")} ₽` : "—"}
                      </span>
                      {isCentral && (
                        <button onClick={() => handleDelete(item.id)} className="text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ConfirmDialogElement />
      <ToastElement />
    </div>
  );
}

function PadSizesTab({ locationFilter }) {
  const { user } = useAuth();
  const isCentral = !!user?.isCentral;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", length_mm: "", width_mm: "", location: "" });
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { showToast, ToastElement } = useToast();

  useEffect(() => {
    api.listPadSizes().then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!form.name.trim() || !form.length_mm || !form.width_mm) return;
    setAdding(true); setError("");
    try {
      const item = await api.createPadSize(form.name.trim(), Number(form.length_mm), Number(form.width_mm), form.location || null);
      setItems(prev => [...prev, item]);
      setForm({ name: "", length_mm: "", width_mm: "", location: "" });
    } catch (err) {
      setError(err.response?.data?.error || "Ошибка");
    } finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    if (!(await confirm("Удалить размер?", { danger: true }))) return;
    try { await api.deletePadSize(id); setItems(prev => prev.filter(i => i.id !== id)); }
    catch { showToast("Ошибка удаления"); }
  };

  return (
    <div className="rounded-2xl bg-white overflow-hidden" style={cardShadow}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Метатарзальный валик — размеры</span>
      </div>
      <div className="p-4 flex flex-col gap-3">
        <p className="text-xs text-slate-400">Длина и ширина задаются в мм — на канвасе валик рисуется с учётом DPI сканера.</p>
        {isCentral ? (
          <div className="flex items-center gap-2">
            <Input className="h-8 text-xs" style={{ flex: 2 }} placeholder="Название (S, M, L...)" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <Input className="h-8 text-xs" style={{ flex: 1 }} type="number" placeholder="Длина мм" min="1" max="200"
              value={form.length_mm} onChange={e => setForm(f => ({ ...f, length_mm: e.target.value }))} />
            <Input className="h-8 text-xs" style={{ flex: 1 }} type="number" placeholder="Ширина мм" min="1" max="200"
              value={form.width_mm} onChange={e => setForm(f => ({ ...f, width_mm: e.target.value }))} />
            <LocationPicker value={form.location} onChange={location => setForm(f => ({ ...f, location }))} className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 max-w-[140px]" />
            <Button size="sm" disabled={adding || !form.name.trim() || !form.length_mm || !form.width_mm} onClick={handleAdd}>
              {adding ? "..." : "+ Добавить"}
            </Button>
          </div>
        ) : <ReadOnlyNote />}
        {error && <p className="text-xs text-red-600">{error}</p>}

        {(() => {
          const visible = items.filter(i => matchesLocationFilter(i, locationFilter));
          if (loading) return <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />;
          if (visible.length === 0) return <p className="text-xs text-slate-400 text-center py-4">Список пуст</p>;
          return (
            <div className="flex flex-col">
              {visible.map(item => (
                <div key={item.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 group">
                  <span className="text-xs font-semibold text-slate-700 flex-1 flex items-center gap-1.5">{item.name}<LocationBadge location={item.location} /></span>
                  <span className="text-xs text-slate-500">{item.length_mm}×{item.width_mm} мм</span>
                  {isCentral && (
                    <button onClick={() => handleDelete(item.id)} className="text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
      </div>
      <ConfirmDialogElement />
      <ToastElement />
    </div>
  );
}

function ExercisesTab({ locationFilter }) {
  const { user } = useAuth();
  const isCentral = !!user?.isCentral;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", description: "", link: "", location: "" });
  const [photoBase64, setPhotoBase64] = useState(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { showToast, ToastElement } = useToast();

  useEffect(() => {
    api.listExercises().then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoBase64(reader.result);
    reader.readAsDataURL(file);
  };

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setAdding(true);
    setError("");
    try {
      const item = await api.createExercise({ ...form, name: form.name.trim(), photoBase64 });
      setItems(prev => [...prev, item]);
      setForm({ name: "", description: "", link: "", location: "" });
      setPhotoBase64(null);
    } catch (err) {
      setError(err.response?.data?.error || "Ошибка");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm("Удалить упражнение?", { danger: true }))) return;
    try {
      await api.deleteExercise(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch {
      showToast("Ошибка удаления");
    }
  };

  return (
    <div className="rounded-2xl bg-white overflow-hidden" style={cardShadow}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Упражнения</span>
      </div>
      <div className="p-4 flex flex-col gap-3">
        {isCentral ? (
          <div className="flex flex-wrap items-start gap-2">
            <Input className="h-8 text-xs flex-1" style={{ minWidth: 160 }} placeholder="Название..." value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <Input className="h-8 text-xs flex-1" style={{ minWidth: 160 }} placeholder="Ссылка (видео, инструкция)..." value={form.link}
              onChange={e => setForm(f => ({ ...f, link: e.target.value }))} />
            <textarea
              className="text-xs rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 resize-none"
              placeholder="Описание..." value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              style={{ flexBasis: "100%", minHeight: 52 }}
            />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoPick} />
            <Button size="sm" variant="outline" type="button" onClick={() => fileRef.current?.click()}>
              {photoBase64 ? "Фото ✓" : "Фото"}
            </Button>
            <LocationPicker value={form.location} onChange={location => setForm(f => ({ ...f, location }))} className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 max-w-[140px]" />
            <Button size="sm" disabled={adding || !form.name.trim()} onClick={handleAdd}>
              {adding ? "..." : "+ Добавить"}
            </Button>
          </div>
        ) : <ReadOnlyNote />}
        {error && <p className="text-xs text-red-600">{error}</p>}

        {(() => {
          const visible = items.filter(i => matchesLocationFilter(i, locationFilter));
          if (loading) return <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />;
          if (visible.length === 0) return <p className="text-xs text-slate-400 text-center py-4">Список пуст</p>;
          return (
            <div className="flex flex-col">
              {visible.map(item => (
                <div key={item.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 group">
                  <ExerciseThumb src={item.hasPhoto ? api.exercisePhotoUrl(item.id) : null} alt={item.name} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">{item.name}<LocationBadge location={item.location} /></p>
                    {item.description && <p className="text-[11px] text-slate-400 truncate">{item.description}</p>}
                  </div>
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline flex-shrink-0">
                      <LinkIcon className="h-3 w-3" /> ссылка
                    </a>
                  )}
                  {isCentral && (
                    <button onClick={() => handleDelete(item.id)} className="text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
      </div>
      <ConfirmDialogElement />
      <ToastElement />
    </div>
  );
}

const TABS = [
  { id: "locations",       label: "Точки продаж" },
  { id: "complaints",      label: "Симптомы" },
  { id: "foot-types",      label: "Тип стопы" },
  { id: "corn-zones",      label: "Натоптыши по зонам" },
  { id: "general-symptoms",label: "Общие симптомы" },
  { id: "diseases",        label: "Заболевания (старый формат)" },
  { id: "shoes",           label: "Типы обуви" },
  { id: "loadtypes",       label: "Типы нагрузки" },
  { id: "insole-products", label: "Стельки" },
  { id: "accessories",     label: "Сопутствующие" },
  { id: "padsizes",        label: "Мет. валик" },
  { id: "exercises",       label: "Упражнения" },
];

export default function FieldConfigPage() {
  const [activeTab, setActiveTab] = useState("locations");
  const [locationFilter, setLocationFilter] = useState("__all__");

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-6">
      <div className="max-w-3xl mx-auto flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-1.5">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                activeTab === t.id ? "bg-blue-50 text-blue-600" : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              {t.label}
            </button>
          ))}
          {activeTab !== "locations" && (
            <LocationFilterSelect value={locationFilter} onChange={setLocationFilter} />
          )}
        </div>

        {activeTab === "locations" && (
          <CatalogTab title="Точки продаж" load={api.listLocations} create={api.createLocation} remove={api.deleteLocation} scopeByLocation={false} renderExtra={(item) => <LocationHeartbeatBadge item={item} />} />
        )}
        {activeTab === "diseases" && (
          <CatalogTab title="Заболевания (старый формат)" load={api.listDiseaseTypes} create={api.createDiseaseType} remove={api.deleteDiseaseType} locationFilter={locationFilter} />
        )}
        {activeTab === "complaints" && (
          <CatalogTab title="Симптомы" load={api.listComplaintTypes} create={api.createComplaintType} remove={api.deleteComplaintType} locationFilter={locationFilter} />
        )}
        {activeTab === "foot-types" && (
          <CatalogTab title="Тип стопы" load={api.listFootTypes} create={api.createFootType} remove={api.deleteFootType} locationFilter={locationFilter} />
        )}
        {activeTab === "corn-zones" && (
          <CatalogTab title="Натоптыши по зонам" load={api.listCornZones} create={api.createCornZone} remove={api.deleteCornZone} locationFilter={locationFilter} />
        )}
        {activeTab === "general-symptoms" && (
          <CatalogTab title="Общие симптомы" load={api.listGeneralSymptoms} create={api.createGeneralSymptom} remove={api.deleteGeneralSymptom} locationFilter={locationFilter} />
        )}
        {activeTab === "shoes" && (
          <CatalogTab title="Типы обуви" load={api.listShoeTypes} create={api.createShoeType} remove={api.deleteShoeType} locationFilter={locationFilter} />
        )}
        {activeTab === "loadtypes" && (
          <CatalogTab title="Типы нагрузки" load={api.listLoadTypes} create={api.createLoadType} remove={api.deleteLoadType} locationFilter={locationFilter} />
        )}
        {activeTab === "insole-products" && <InsolesTab locationFilter={locationFilter} />}
        {activeTab === "accessories" && <AccessoriesTab locationFilter={locationFilter} />}
        {activeTab === "padsizes" && <PadSizesTab locationFilter={locationFilter} />}
        {activeTab === "exercises" && <ExercisesTab locationFilter={locationFilter} />}
      </div>
    </div>
  );
}
